/** @jsxImportSource @emotion/react */
import { useState, useRef, useEffect } from "react";
import {
  accessAPI,
  Titulo,
  Formulario,
  CampoInformacion,
  Boton,
  useIsMobile,
  TablaFiltrable
} from "../../../shared";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Menu } from "primereact/menu";
import { Sidebar } from "../../../shared";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { Divider } from "primereact/divider";
import { confirmDialog } from "primereact/confirmdialog";

export default function CardEmpleados({
  empleados,
  clienteId,
  onEmpleadoCreado,
  onEmpleadoEditado,
  onEmpleadoEliminado
}) {
  const [sidebarDetallesVisible, setSidebarDetallesVisible] = useState(false);
  const [sidebarEditarVisible, setSidebarEditarVisible] = useState(false);
  const [sidebarNuevoVisible, setSidebarNuevoVisible] = useState(false);
  const [selectedEmpleado, setSelectedEmpleado] = useState(null);
  const [loaderEditar, setLoaderEditar] = useState(false);
  const [loaderNuevo, setLoaderNuevo] = useState(false);
  // Opciones del dropdown de sitio (para asignar al empleado)
  const [opcionesSitios, setOpcionesSitios] = useState([]);

  useEffect(() => {
    if (!clienteId) return;
    accessAPI(
      "GET",
      `admin/sitio/cliente/${clienteId}`,
      null,
      (respuesta) => {
        setOpcionesSitios(
          (respuesta || []).map((s) => ({
            value: s.id,
            etiqueta: `${s.nombre || s.sitioTipo?.nombre || "Sitio"} - ${
              s.direccion || ""
            }`
          }))
        );
      },
      () => setOpcionesSitios([])
    );
  }, [clienteId]);

  const menuRef = useRef(null);
  const toast = useRef(null);
  const isMobile = useIsMobile();

  function eliminarEmpleado(empleado) {
    accessAPI(
      "DELETE",
      `admin/cliente/empleado/${empleado.id}`,
      null,
      (respuesta) => {
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: respuesta[0]?.msg || "Empleado eliminado"
        });
        if (onEmpleadoEliminado) {
          onEmpleadoEliminado(empleado.id);
        }
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al eliminar empleado",
          sticky: true
        });
      }
    );
  }

  const menuItems = [
    {
      label: "Ver detalles",
      icon: "pi pi-eye",
      command: () => {
        if (selectedEmpleado) {
          setSidebarDetallesVisible(true);
        }
      }
    },
    {
      label: "Editar empleado",
      icon: "pi pi-pencil",
      command: () => {
        if (selectedEmpleado) {
          setSidebarEditarVisible(true);
        }
      }
    },
    {
      label: "Eliminar empleado",
      icon: "pi pi-trash",
      command: () => {
        if (!selectedEmpleado) return;
        const empleado = selectedEmpleado;
        confirmDialog({
          message: `¿Eliminar a ${empleado.nombre} ${empleado.apellido}? Esta acción no se puede deshacer.`,
          header: "Confirmar eliminación",
          icon: "pi pi-exclamation-triangle",
          acceptLabel: "Eliminar",
          rejectLabel: "Cancelar",
          acceptClassName: "p-button-danger",
          accept: () => eliminarEmpleado(empleado)
        });
      }
    }
  ];

  function nuevoEmpleadoSubmit(formData) {
    setLoaderNuevo(true);
    accessAPI(
      "POST",
      `admin/cliente/empleadocliente/${clienteId}`,
      formData,
      (respuesta) => {
        setLoaderNuevo(false);
        setSidebarNuevoVisible(false);
        if (onEmpleadoCreado) {
          onEmpleadoCreado(respuesta);
        }
      },
      (respuesta) => {
        setLoaderNuevo(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0].msg,
          sticky: true
        });
      }
    );
  }

  const nuevoEmpleadoFormConfig = {
    secciones: [
      {
        nombre: "Nuevo empleado",
        campos: [
          {
            nombre: "nombreresponsable",
            tipoitemformularioid: 1,
            etiqueta: "Nombre",
            requerido: true
          },
          {
            nombre: "apellidoresponsable",
            tipoitemformularioid: 1,
            etiqueta: "Apellido",
            requerido: true
          },
          {
            nombre: "emailresponsable",
            tipoitemformularioid: 2,
            etiqueta: "Email",
            requerido: true
          },
          {
            nombre: "telefonoresponsable",
            tipoitemformularioid: 1,
            etiqueta: "Celular"
          },
          {
            nombre: "cargoresponsable",
            tipoitemformularioid: 1,
            etiqueta: "Cargo"
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Crear empleado",
        callback: nuevoEmpleadoSubmit,
        validar: true
      }
    ]
  };

  function editarEmpleadoSubmit(formData) {
    setLoaderEditar(true);
    accessAPI(
      "PUT",
      `admin/cliente/empleado/${selectedEmpleado.id}`,
      formData,
      (respuesta) => {
        setLoaderEditar(false);
        setSidebarEditarVisible(false);
        const empleadoActualizado = respuesta[0].empleado;
        setSelectedEmpleado(empleadoActualizado);
        if (onEmpleadoEditado) {
          onEmpleadoEditado(empleadoActualizado);
        }
      },
      (respuesta) => {
        setLoaderEditar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0].msg,
          sticky: true
        });
      }
    );
  }

  const editarEmpleadoFormConfig = selectedEmpleado
    ? {
        secciones: [
          {
            nombre: "Editar empleado",
            campos: [
              {
                nombre: "nombre",
                tipoitemformularioid: 1,
                etiqueta: "Nombre",
                requerido: true,
                respuesta: selectedEmpleado.nombre
              },
              {
                nombre: "apellido",
                tipoitemformularioid: 1,
                etiqueta: "Apellido",
                requerido: true,
                respuesta: selectedEmpleado.apellido
              },
              {
                nombre: "email",
                tipoitemformularioid: 2,
                etiqueta: "Email",
                requerido: true,
                respuesta: selectedEmpleado.email
              },
              {
                nombre: "celular",
                tipoitemformularioid: 1,
                etiqueta: "Celular",
                respuesta: selectedEmpleado.celular
              },
              {
                nombre: "cargo",
                tipoitemformularioid: 1,
                etiqueta: "Cargo",
                respuesta: selectedEmpleado.cargo
              },
              {
                nombre: "sitioId",
                tipoitemformularioid: 5,
                etiqueta: "Sitio",
                opciones: opcionesSitios,
                respuesta: selectedEmpleado.sitioId
              },
              {
                nombre: "notas",
                tipoitemformularioid: 11,
                etiqueta: "Notas",
                respuesta: selectedEmpleado.notas
              }
            ]
          }
        ],
        botones: [
          {
            etiqueta: "Guardar cambios",
            callback: editarEmpleadoSubmit,
            validar: true
          }
        ]
      }
    : null;

  function aprobarUsuario(empleado) {
    if (!empleado.usuario?.id) return;
    confirmDialog({
      message: `¿Aprobar al usuario ${empleado.nombre} ${empleado.apellido} (${empleado.email})? Se le enviará un correo con instrucciones para crear su contraseña.`,
      header: "Aprobar usuario",
      icon: "pi pi-user-plus",
      acceptLabel: "Aprobar",
      rejectLabel: "Cancelar",
      accept: () => {
        accessAPI(
          "PUT",
          `admin/usuario/aprobar/${empleado.usuario.id}`,
          null,
          () => {
            toast.current?.show({
              severity: "success",
              summary: "Usuario aprobado",
              detail: `Se envió el correo a ${empleado.email}`,
              life: 3000
            });
            // Actualizar el estado local del empleado
            empleado.usuario.aprobado = true;
            onEmpleadoEditado?.(empleado);
          },
          (respuesta) => {
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: respuesta[0]?.msg || "Error al aprobar usuario",
              sticky: true
            });
          }
        );
      }
    });
  }

  const nombreCompletoTemplate = (rowData) => {
    return `${rowData.nombre} ${rowData.apellido}`;
  };

  const accionesTemplate = (rowData) => {
    return (
      <Boton
        texto
        negro
        callback={(e) => {
          e.stopPropagation();
          setSelectedEmpleado(rowData);
          menuRef.current.toggle(e);
        }}
      >
        <i className="pi pi-ellipsis-v" />
      </Boton>
    );
  };

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <Menu model={menuItems} popup ref={menuRef} />

      {/* Sidebar detalles */}
      <Sidebar
        visible={sidebarDetallesVisible}
        position="right"
        onHide={() => setSidebarDetallesVisible(false)}
        className="w-full lg:w-30rem md:w-30rem"
      >
        {selectedEmpleado && (
          <>
            <Titulo>
              {selectedEmpleado.nombre} {selectedEmpleado.apellido}
            </Titulo>
            <div css={{ display: "flex", flexWrap: "wrap" }}>
              <CampoInformacion nombreCampo="Nombre">
                {selectedEmpleado.nombre}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Apellido">
                {selectedEmpleado.apellido}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Email">
                {selectedEmpleado.email}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Celular">
                {selectedEmpleado.celular}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Cargo">
                {selectedEmpleado.cargo}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Sitio">
                {selectedEmpleado.sitio
                  ? selectedEmpleado.sitio.nombre ||
                    selectedEmpleado.sitio.direccion
                  : "-"}
              </CampoInformacion>
              <Divider />
              <CampoInformacion nombreCampo="Notas" anchoCompleto>
                <span css={{ whiteSpace: "pre-wrap" }}>
                  {selectedEmpleado.notas}
                </span>
              </CampoInformacion>
            </div>
          </>
        )}
      </Sidebar>

      {/* Sidebar editar */}
      <Sidebar
        visible={sidebarEditarVisible}
        position="right"
        onHide={() => setSidebarEditarVisible(false)}
        className="w-full lg:w-30rem md:w-30rem"
      >
        {editarEmpleadoFormConfig && (
          <Formulario config={editarEmpleadoFormConfig} loader={loaderEditar} />
        )}
      </Sidebar>

      {/* Sidebar nuevo empleado */}
      <Sidebar
        visible={sidebarNuevoVisible}
        position="right"
        onHide={() => setSidebarNuevoVisible(false)}
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Formulario config={nuevoEmpleadoFormConfig} loader={loaderNuevo} />
      </Sidebar>

      <Card>
        <Titulo
          botones={
            <Boton
              texto
              negro
              chico
              callback={() => setSidebarNuevoVisible(true)}
            >
              <i className="pi pi-plus" />
            </Boton>
          }
        >
          Empleados
        </Titulo>
        <TablaFiltrable conFiltros={false}
          value={empleados}
          size="small"
          emptyMessage="No hay empleados registrados"
          nombreArchivo="empleados_cliente"
        >
          <Column header="Nombre" body={nombreCompletoTemplate} />
          {!isMobile && <Column field="cargo" header="Cargo" />}
          <Column
            body={(rowData) => {
              if (!rowData.usuario) return null;
              if (rowData.usuario.aprobado) return null;
              return (
                <Tag
                  value="Usuario solicitado"
                  severity="warning"
                  css={{
                    cursor: "pointer",
                    fontSize: "0.75em",
                    "&:hover": { opacity: 0.8 }
                  }}
                  onClick={() => aprobarUsuario(rowData)}
                  title="Click para aprobar"
                />
              );
            }}
            style={{ width: "60px", textAlign: "center" }}
          />
          <Column
            body={accionesTemplate}
            style={{ width: "60px", textAlign: "center" }}
          />
        </TablaFiltrable>
      </Card>
    </>
  );
}
